import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { environment } from "./environment";
import { getToken } from "./App";
import { api } from "./utils/api";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentIcon,
  XMarkIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";

const EMRTypes = React.memo(() => {
  const [emrTypes, setEmrTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmrType, setSelectedEmrType] = useState(null);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    session_type: "",
    documentation_methods: "",
    files: [],
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();
  // Add state for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emrTypeToDelete, setEmrTypeToDelete] = useState(null);
  // Add keptFiles state for edit mode
  const [keptFiles, setKeptFiles] = useState([]);
  // Add originalFiles state to track what files were there initially
  const [originalFiles, setOriginalFiles] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState("types");

  // Field-EMR response mapping state (stored locally until API is available)
  const [responseMappings, setResponseMappings] = useState([]);
  const [responseForm, setResponseForm] = useState({
    field_name: "",
    emr_type_id: "",
    response: "response 1",
  });
  const [responseFormError, setResponseFormError] = useState("");
  const [responseLoading, setResponseLoading] = useState(false);
  const [responseError, setResponseError] = useState("");
  const [showDeleteResponseModal, setShowDeleteResponseModal] = useState(false);
  const [responseMappingToDelete, setResponseMappingToDelete] = useState(null);
  const [responseDeleteLoading, setResponseDeleteLoading] = useState(false);

  // Documentation Methods state
  const [documentationMethods, setDocumentationMethods] = useState([]);
  const [documentationMethodsLoaded, setDocumentationMethodsLoaded] = useState(false);
  const [isLoadingDocumentationMethods, setIsLoadingDocumentationMethods] = useState(false);

  // Memoized documentation methods options to prevent unnecessary re-renders
  const documentationMethodsOptions = useMemo(() => {
    return documentationMethods.map((method) => (
      <option key={method.id} value={method.id}>
        {method.name}
      </option>
    ));
  }, [documentationMethods]);

  // Session Types options - memoized to prevent recreation on every render
  const sessionTypes = useMemo(() => [
    "Individual Therapy",
    "Group Therapy", 
    "Family Therapy",
    "Couples Therapy",
    "Assessment",
    "Consultation",
    "Crisis Intervention",
    "Case Management",
    "Other"
  ], []);

  // EMR Fields state
  const [emrFields, setEmrFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [fieldsError, setFieldsError] = useState("");
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [showEditFieldModal, setShowEditFieldModal] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [fieldForm, setFieldForm] = useState({ 
    name: "", 
    type: "text",
    analyzable: "for_analyzing",
    api_name: "",
    dropdown_values: "",
    instructions: ""
  });
  const [fieldFormLoading, setFieldFormLoading] = useState(false);
  const [fieldFormError, setFieldFormError] = useState("");
  const [fieldFormSuccess, setFieldFormSuccess] = useState("");
  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);

  // Sorting state for EMR Fields
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Sorting state for EMR Types - no default sort
  const [sortEmrTypeField, setSortEmrTypeField] = useState(null);
  const [sortEmrTypeDirection, setSortEmrTypeDirection] = useState('asc');

  const fieldTypes = [
    { value: "text", label: "Text", color: "bg-blue-100 text-blue-800" },
    { value: "date", label: "Date", color: "bg-green-100 text-green-800" },
    { value: "time", label: "Time", color: "bg-amber-100 text-amber-800" },
    { value: "datetime", label: "DateTime", color: "bg-indigo-100 text-indigo-800" },
    { value: "textarea", label: "Text Area", color: "bg-purple-100 text-purple-800" },
    { value: "boolean", label: "Boolean", color: "bg-red-100 text-red-800" },
    { value: "dropdown", label: "Dropdown", color: "bg-gradient-to-r from-blue-400 to-indigo-500 text-white font-medium" }
  ];

  const analyzableOptions = [
    { value: "not_for_analyzing", label: "Not For Analyzing", color: "bg-gray-100 text-gray-800" },
    { value: "for_analyzing", label: "For Analyzing", color: "bg-green-100 text-green-800" }
  ];

  const responseOptions = ["response 1", "response 2", "response 3"];

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedFields = () => {
    if (!sortField) return emrFields;

    return [...emrFields].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle different data types
      if (sortField === 'created_at' || sortField === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Sorting functions for EMR Types
  const handleEmrTypeSort = (field) => {
    if (sortEmrTypeField === field) {
      // Cycle through: asc -> desc -> unsorted (null)
      if (sortEmrTypeDirection === 'asc') {
        setSortEmrTypeDirection('desc');
      } else {
        // Clear sort - go back to unsorted
        setSortEmrTypeField(null);
        setSortEmrTypeDirection('asc');
      }
    } else {
      setSortEmrTypeField(field);
      setSortEmrTypeDirection('asc');
    }
  };

  const getSortedEmrTypes = () => {
    // If no explicit sort is set, default to newest first (created_at desc)
    if (!sortEmrTypeField) {
      return [...emrTypes].sort((a, b) => {
        const aDate = new Date(a.created_at || 0);
        const bDate = new Date(b.created_at || 0);
        return bDate - aDate; // Descending (newest first)
      });
    }

    return [...emrTypes].sort((a, b) => {
      let aValue = a[sortEmrTypeField];
      let bValue = b[sortEmrTypeField];

      // Handle special cases
      if (sortEmrTypeField === 'name') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (sortEmrTypeField === 'session_type') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (sortEmrTypeField === 'documentation_method_id') {
        // Get the documentation method name for sorting
        const aMethod = documentationMethods.find(m => m.id === aValue);
        const bMethod = documentationMethods.find(m => m.id === bValue);
        aValue = (aMethod?.name || aValue || '').toLowerCase();
        bValue = (bMethod?.name || bValue || '').toLowerCase();
      } else if (sortEmrTypeField === 'user_id') {
        // Get the user name for sorting
        const aUser = users.find(u => u.id === aValue);
        const bUser = users.find(u => u.id === bValue);
        aValue = (aUser?.full_name || aUser?.name || '').toLowerCase();
        bValue = (bUser?.full_name || bUser?.name || '').toLowerCase();
      } else if (sortEmrTypeField === 'status') {
        aValue = (aValue || 'draft').toLowerCase();
        bValue = (bValue || 'draft').toLowerCase();
      } else if (sortEmrTypeField === 'created_at') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortEmrTypeDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortEmrTypeDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getEmrTypeSortIcon = (field) => {
    if (sortEmrTypeField !== field) {
      // Not sorted - show neutral sort icon
      return (
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    // Sorted - show direction icon
    return sortEmrTypeDirection === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Fetch Documentation Methods - optimized with caching and duplicate call prevention
  const fetchDocumentationMethods = useCallback(async () => {
    // If already loaded or currently loading, don't fetch again
    if (documentationMethodsLoaded || isLoadingDocumentationMethods) {
      return;
    }

    setIsLoadingDocumentationMethods(true);
    
    try {
      const response = await fetch(`${environment.apiUrl}/documentation-methods/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentationMethods(data);
        setDocumentationMethodsLoaded(true);
      } else {
        console.error("Documentation Methods fetch error:", await response.json());
        setDocumentationMethods([]);
      }
    } catch (err) {
      console.error("Error fetching documentation methods:", err);
      setDocumentationMethods([]);
    } finally {
      setIsLoadingDocumentationMethods(false);
    }
  }, [documentationMethodsLoaded, isLoadingDocumentationMethods]);

  // Fetch EMR Types
  const fetchEmrTypes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${environment.apiUrl}/emr-types/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEmrTypes(data);
      } else {
        const errorData = await response.json();
        console.error("EMR Types fetch error:", errorData);
        setError(`Failed to fetch EMR types: ${errorData.detail || errorData.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Users
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${environment.apiUrl}/users`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        console.error("Users fetch error:", await response.json());
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // Fetch EMR Fields
  const fetchEmrFields = async () => {
    setLoadingFields(true);
    setFieldsError("");
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types-fields/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setEmrFields(data);
      } else {
        setFieldsError("Failed to fetch EMR fields");
      }
    } catch (err) {
      setFieldsError("Network error");
    } finally {
      setLoadingFields(false);
    }
  };

  useEffect(() => {
    fetchEmrTypes();
    fetchDocumentationMethods();
    fetchUsers();
  }, [fetchDocumentationMethods]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts).forEach(timeoutId => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, []);

  useEffect(() => {
    if (activeTab === "fields") {
      fetchEmrFields();
    }
  }, [activeTab]);

  useEffect(() => {
    const fetchResponseMappings = async () => {
      if (activeTab !== "responses") {
        return;
      }

      setResponseLoading(true);
      setResponseError("");

      try {
        const data = await api.getEMRTypeFieldResponses();
        setResponseMappings(Array.isArray(data) ? data : []);
      } catch (err) {
        setResponseError("Failed to load EMR type field responses.");
        setResponseMappings([]);
      } finally {
        setResponseLoading(false);
      }
    };

    fetchResponseMappings();
  }, [activeTab]);

  // Handle file input change - optimized with useCallback
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
    // Reset the file input so user can add the same file again if needed
    e.target.value = null;
  }, []);

  // Remove file from form - optimized with useCallback
  const removeFile = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  }, []);

  // Optimized form data change handler to prevent unnecessary re-renders
  const handleFormDataChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Debounced dropdown change handler to prevent rapid clicking issues
  const [debounceTimeouts, setDebounceTimeouts] = useState({});
  
  const handleDebouncedFormDataChange = useCallback((field, value) => {
    // Clear existing timeout for this field
    if (debounceTimeouts[field]) {
      clearTimeout(debounceTimeouts[field]);
    }
    
    // Set new timeout
    const timeoutId = setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
      setDebounceTimeouts(prev => {
        const newTimeouts = { ...prev };
        delete newTimeouts[field];
        return newTimeouts;
      });
    }, 100); // 100ms debounce
    
    setDebounceTimeouts(prev => ({
      ...prev,
      [field]: timeoutId
    }));
  }, [debounceTimeouts]);


  // Remove existing file from EMR type
  const removeExistingFile = async (emrTypeId, fileIndex) => {
    try {
      const emrType = emrTypes.find(et => et.id === emrTypeId);
      if (!emrType || !emrType.files) return;

      const updatedFiles = emrType.files.filter((_, i) => i !== fileIndex);
      
      const response = await fetch(`${environment.apiUrl}/emr-types/${emrTypeId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: emrType.name,
          session_type: emrType.session_type,
          documentation_methods: emrType.documentation_methods,
          files: updatedFiles
        }),
      });

      if (response.ok) {
        fetchEmrTypes(); // Refresh the list
      } else {
        setError("Failed to remove file");
      }
    } catch (err) {
      setError("Network error");
    }
  };

  // Create EMR Type
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError("");

    // Validate required fields
    if (!formData.session_type || formData.session_type.trim() === "") {
      setFormError("Session type is required");
      setFormLoading(false);
      return;
    }
    if (!formData.documentation_methods || formData.documentation_methods.trim() === "") {
      setFormError("Documentation method is required");
      setFormLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("session_type", formData.session_type);
      formDataToSend.append("documentation_method_id", formData.documentation_methods);
      
      // For create
      if (formData.files.length > 0) {
        formData.files.forEach((file, index) => {
          formDataToSend.append(`files`, file);
        });
      }

      const response = await fetch(`${environment.apiUrl}/emr-types/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setShowCreateModal(false);
        setFormData({ name: "", session_type: "", documentation_methods: "", files: [] });
        fetchEmrTypes();
      } else {
        const data = await response.json();
        setFormError(
          typeof data.detail === "string"
            ? data.detail
            : JSON.stringify(data.detail || data)
        );
      }
    } catch (err) {
      setFormError("Network error");
    } finally {
      setFormLoading(false);
    }
  };

  // Edit EMR Type
  const handleEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("session_type", formData.session_type);
      formDataToSend.append("documentation_method_id", formData.documentation_methods);
      
      // Check if any files were removed by comparing keptFiles with originalFiles
      const filesWereRemoved = keptFiles.length < originalFiles.length;
      
      // Only send clear_files: true if files were actually removed
      if (filesWereRemoved) {
        formDataToSend.append("clear_files", "true");
      }
      
      // Only send new files if user added them
      if (formData.files.length > 0) {
        formData.files.forEach((file) => {
          formDataToSend.append("files", file);
        });
      }

      const response = await fetch(`${environment.apiUrl}/emr-types/${selectedEmrType.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setShowEditModal(false);
        setSelectedEmrType(null);
        setFormData({ name: "", session_type: "", documentation_methods: "", files: [] });
        setKeptFiles([]);
        setOriginalFiles([]);
        fetchEmrTypes();
      } else {
        const data = await response.json();
        setFormError(
          typeof data.detail === "string"
            ? data.detail
            : JSON.stringify(data.detail || data)
        );
      }
    } catch (err) {
      setFormError("Network error");
    } finally {
      setFormLoading(false);
    }
  };

  // Delete EMR Type
  const handleDelete = async () => {
    if (!emrTypeToDelete) return;
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types/${emrTypeToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        fetchEmrTypes();
      } else {
        setError("Failed to delete EMR type");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setShowDeleteModal(false);
      setEmrTypeToDelete(null);
    }
  };

  // Open edit modal
  const openEditModal = (emrType) => {
    setSelectedEmrType(emrType);
    setFormData({
      name: emrType.name,
      session_type: emrType.session_type || "",
      documentation_methods: emrType.documentation_method_id || "",
      files: [],
    });
    const files = emrType.files ? [...emrType.files] : [];
    setKeptFiles(files);
    setOriginalFiles(files); // Store original files to compare later
    setShowEditModal(true);
  };

  // Remove file from keptFiles (frontend only)
  const removeKeptFile = (index) => {
    setKeptFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: "", session_type: "", documentation_methods: "", files: [] });
    setFormError("");
    setKeptFiles([]);
    setOriginalFiles([]);
    setFieldForm({ name: "", type: "text", analyzable: "for_analyzing", api_name: "", dropdown_values: "", instructions: "" });
  };

  // Field management functions
  const openAddFieldModal = () => {
    setFieldForm({ name: "", type: "text", analyzable: "for_analyzing", api_name: "", dropdown_values: "", instructions: "" });
    setFieldFormError("");
    setFieldFormSuccess("");
    setShowAddFieldModal(true);
  };

  const openEditFieldModal = (field) => {
    setSelectedField(field);
    setFieldForm({ 
      name: field.name, 
      type: field.type,
      analyzable: field.analyzable || "not_for_analyzing",
      api_name: field.api_name || "",
      dropdown_values: field.dropdown_values || "",
      instructions: field.instructions || ""
    });
    setFieldFormError("");
    setFieldFormSuccess("");
    setShowEditFieldModal(true);
  };

  const openDeleteFieldModal = (field) => {
    setFieldToDelete(field);
    setShowDeleteFieldModal(true);
  };

  const handleCreateField = async (e) => {
    e.preventDefault();
    setFieldFormLoading(true);
    setFieldFormError("");
    setFieldFormSuccess("");

    // Validate dropdown values if type is dropdown
    if (fieldForm.type === "dropdown" && (!fieldForm.dropdown_values || !fieldForm.dropdown_values.trim())) {
      setFieldFormError("Dropdown values are required when type is dropdown.");
      setFieldFormLoading(false);
      return;
    }

    try {
      // Don't send api_name since it's system managed
      const { api_name, ...fieldDataToSend } = fieldForm;
      
      // Only include dropdown_values if type is dropdown
      if (fieldForm.type !== "dropdown") {
        delete fieldDataToSend.dropdown_values;
      }
      
      const response = await fetch(`${environment.apiUrl}/emr-types-fields/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(fieldDataToSend),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setFieldFormSuccess(data.message || "Field created successfully!");
        setTimeout(() => {
          setShowAddFieldModal(false);
          setFieldForm({ name: "", type: "text", analyzable: "for_analyzing", api_name: "", dropdown_values: "", instructions: "" });
          setFieldFormSuccess("");
          fetchEmrFields();
        }, 1500);
      } else {
        setFieldFormError(data.detail || data.message || "Failed to create field.");
      }
    } catch (err) {
      setFieldFormError("Network error");
    } finally {
      setFieldFormLoading(false);
    }
  };

  const handleUpdateField = async (e) => {
    e.preventDefault();
    setFieldFormLoading(true);
    setFieldFormError("");
    setFieldFormSuccess("");

    // Validate dropdown values if type is dropdown
    if (fieldForm.type === "dropdown" && (!fieldForm.dropdown_values || !fieldForm.dropdown_values.trim())) {
      setFieldFormError("Dropdown values are required when type is dropdown.");
      setFieldFormLoading(false);
      return;
    }

    try {
      // Don't send api_name since it's system managed
      const { api_name, ...fieldDataToSend } = fieldForm;
      
      // Only include dropdown_values if type is dropdown
      if (fieldForm.type !== "dropdown") {
        delete fieldDataToSend.dropdown_values;
      }
      
      const response = await fetch(`${environment.apiUrl}/emr-types-fields/${selectedField.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(fieldDataToSend),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setFieldFormSuccess(data.message || "Field updated successfully!");
        setTimeout(() => {
          setShowEditFieldModal(false);
          setSelectedField(null);
          setFieldForm({ name: "", type: "text", analyzable: "for_analyzing", api_name: "", dropdown_values: "", instructions: "" });
          setFieldFormSuccess("");
          fetchEmrFields();
        }, 1500);
      } else {
        setFieldFormError(data.detail || data.message || "Failed to update field.");
      }
    } catch (err) {
      setFieldFormError("Network error");
    } finally {
      setFieldFormLoading(false);
    }
  };

  const handleDeleteField = async () => {
    if (!fieldToDelete) return;
    
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types-fields/${fieldToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        setShowDeleteFieldModal(false);
        setFieldToDelete(null);
        fetchEmrFields();
      } else {
        setFieldsError("Failed to delete field");
      }
    } catch (err) {
      setFieldsError("Network error");
    }
  };

  const handleCreateResponseMapping = async (e) => {
    e.preventDefault();
    setResponseFormError("");

    if (!responseForm.field_name.trim()) {
      setResponseFormError("Field name is required.");
      return;
    }

    if (!responseForm.emr_type_id) {
      setResponseFormError("EMR type is required.");
      return;
    }

    setResponseLoading(true);

    try {
      const created = await api.createEMRTypeFieldResponse({
        field_name: responseForm.field_name.trim(),
        emr_type_id: responseForm.emr_type_id,
        response_value: responseForm.response,
      });

      setResponseMappings((prev) => [created, ...prev]);
      setResponseForm({
        field_name: "",
        emr_type_id: "",
        response: "response 1",
      });
    } catch (err) {
      setResponseFormError(
        err?.message || "Failed to create EMR type field response."
      );
    } finally {
      setResponseLoading(false);
    }
  };

  const openDeleteResponseMappingModal = (mapping) => {
    setResponseError("");
    setResponseMappingToDelete(mapping);
    setShowDeleteResponseModal(true);
  };

  const handleDeleteResponseMapping = async () => {
    if (!responseMappingToDelete) return;

    setResponseError("");
    setResponseDeleteLoading(true);

    try {
      await api.deleteEMRTypeFieldResponse(responseMappingToDelete.id);
      setResponseMappings((prev) =>
        prev.filter((item) => item.id !== responseMappingToDelete.id)
      );
      setShowDeleteResponseModal(false);
      setResponseMappingToDelete(null);
    } catch (err) {
      setResponseError(
        err?.message || "Failed to delete EMR type field response."
      );
    } finally {
      setResponseDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Loading EMR types...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header with Tab Navigation */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex bg-slate-100 rounded-xl p-1 shadow-sm">
          <button
            className={`px-6 py-3 font-semibold text-sm rounded-lg transition-all duration-200 ${
              activeTab === "types"
                ? "bg-white text-blue-600 shadow-md"
                : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
            }`}
            onClick={() => setActiveTab("types")}
          >
            EMR Types
          </button>
          <button
            className={`px-6 py-3 font-semibold text-sm rounded-lg transition-all duration-200 ${
              activeTab === "fields"
                ? "bg-white text-blue-600 shadow-md"
                : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
            }`}
            onClick={() => setActiveTab("fields")}
          >
            EMR Type Fields
          </button>
          <button
            className={`px-6 py-3 font-semibold text-sm rounded-lg transition-all duration-200 ${
              activeTab === "responses"
                ? "bg-white text-blue-600 shadow-md"
                : "text-slate-600 hover:text-slate-800 hover:bg-slate-50"
            }`}
            onClick={() => setActiveTab("responses")}
          >
            EMR Type Responses
          </button>
        </div>
        {activeTab === "types" && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
          >
            <PlusIcon className="w-5 h-5" />
            Add EMR Type
          </button>
        )}
      </div>

      {activeTab === "types" && (
        <>
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 shadow-sm">
              {error}
            </div>
          )}

          {/* EMR Types List */}
                      <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              {emrTypes.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DocumentIcon className="w-10 h-10 text-slate-400" />
                  </div>
                  <p className="text-xl font-semibold text-slate-900 mb-2">No EMR types found</p>
                  <p className="text-slate-600">Create your first EMR type to get started</p>
                </div>
              ) : (
                <div className="rounded-xl overflow-x-auto">
                  <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                      <th 
                        className="px-3 py-4 text-left font-semibold text-slate-800 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none" 
                        style={{ width: '12%' }}
                        onClick={() => handleEmrTypeSort('name')}
                      >
                        <div className="flex items-center gap-2">
                          Name
                          {getEmrTypeSortIcon('name')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-4 text-left font-semibold text-slate-800 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none" 
                        style={{ width: '12%' }}
                        onClick={() => handleEmrTypeSort('session_type')}
                      >
                        <div className="flex items-center gap-2">
                          Session Type
                          {getEmrTypeSortIcon('session_type')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-4 text-left font-semibold text-slate-800 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none" 
                        style={{ width: '14%' }}
                        onClick={() => handleEmrTypeSort('documentation_method_id')}
                      >
                        <div className="flex items-center gap-2">
                          Documentation Methods
                          {getEmrTypeSortIcon('documentation_method_id')}
                        </div>
                      </th>
                      <th className="px-3 py-4 text-left font-semibold text-slate-800">
                        Files
                      </th>
                      <th 
                        className="px-3 py-4 text-left font-semibold text-slate-800 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none" 
                        style={{ width: '9%' }}
                        onClick={() => handleEmrTypeSort('user_id')}
                      >
                        <div className="flex items-center gap-2">
                          User
                          {getEmrTypeSortIcon('user_id')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-4 text-left font-semibold text-slate-800 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none" 
                        style={{ width: '8%' }}
                        onClick={() => handleEmrTypeSort('status')}
                      >
                        <div className="flex items-center gap-2">
                          Status
                          {getEmrTypeSortIcon('status')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-4 text-left font-semibold text-slate-800 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none" 
                        style={{ width: '8%' }}
                        onClick={() => handleEmrTypeSort('created_at')}
                      >
                        <div className="flex items-center gap-2">
                          Created
                          {getEmrTypeSortIcon('created_at')}
                        </div>
                      </th>
                      <th className="px-3 py-4 text-right font-semibold text-slate-800" style={{ width: '10%' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedEmrTypes().map((emrType, index) => (
                      <tr
                        key={emrType.id}
                        className={`border-b border-slate-100 transition-all duration-200 ${
                          index % 2 === 0 ? "bg-white" : "bg-slate-50"
                        } hover:bg-blue-50 hover:shadow-sm cursor-pointer`}
                        onClick={() => navigate(`/emr-types/${emrType.id}`)}
                      >
                        <td className="px-3 py-4 font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                              {emrType.name ? emrType.name[0].toUpperCase() : "E"}
                            </div>
                            <span className="font-semibold">{emrType.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-4 text-slate-700">
                          <div className="text-sm">
                            {emrType.session_type || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm text-slate-700 truncate whitespace-nowrap">
                            {emrType.documentation_method_id 
                              ? (documentationMethods.length > 0 
                                  ? documentationMethods.find(method => method.id === emrType.documentation_method_id)?.name || emrType.documentation_method_id
                                  : emrType.documentation_method_id)
                              : "—"}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="flex flex-wrap gap-1">
                            {emrType.files && emrType.files.length > 0 ? (
                              emrType.files.map((file, fileIndex) => {
                                const fileName = file.filename || file.name || `File ${fileIndex + 1}`;
                                return (
                                  <div key={fileIndex} className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-lg text-xs font-medium shadow-sm" style={{ maxWidth: '100%' }}>
                                    <PaperClipIcon className="w-3 h-3 flex-shrink-0" />
                                    <span 
                                      className="truncate block overflow-hidden text-ellipsis whitespace-nowrap" 
                                      title={fileName}
                                    >
                                      {fileName}
                                    </span>
                                  </div>
                                );
                              })
                            ) : (
                              <span className="text-slate-400 text-sm bg-slate-100 px-2 py-1 rounded-lg">No files</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm text-slate-700 truncate">
                            {emrType.user_id 
                              ? (users.find(u => u.id === emrType.user_id)?.full_name || "—")
                              : "—"}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                              (emrType.status || '').toLowerCase() === 'draft' ? 'bg-gray-100 text-gray-700 border border-gray-300' :
                              (emrType.status || '').toLowerCase() === 'analyzed' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                              (emrType.status || '').toLowerCase() === 'generated' ? 'bg-purple-100 text-purple-700 border border-purple-300' :
                              (emrType.status || '').toLowerCase() === 'active' ? 'bg-green-100 text-green-700 border border-green-300' :
                              'bg-slate-100 text-slate-700 border border-slate-300'
                            }`}>
                              {(emrType.status || 'draft').charAt(0).toUpperCase() + (emrType.status || 'draft').slice(1).toLowerCase()}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-4 text-slate-500">
                          <div className="text-sm truncate">
                            {new Date(emrType.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-right">
                          <div className="flex justify-end gap-0.5">
                            <button
                              onClick={e => { e.stopPropagation(); openEditModal(emrType); }}
                              className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                              title="Edit EMR type"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); setShowDeleteModal(true); setEmrTypeToDelete(emrType); }}
                              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                              title="Delete EMR type"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "fields" && (
        <div className="w-full">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-3xl font-bold text-slate-800 mb-2">EMR Type Fields</h3>
              <p className="text-slate-600 text-lg">Manage the fields that will be captured for EMR types.</p>
            </div>
            <button
              onClick={openAddFieldModal}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
            >
              <PlusIcon className="w-5 h-5" />
              Add Field
            </button>
          </div>
          
          {fieldsError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm shadow-sm">
              {fieldsError}
            </div>
          )}

          {loadingFields ? (
            <div className="text-center py-12 text-slate-500">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-lg">Loading fields...</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
              <div className="rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                      <th 
                        className="px-4 py-4 text-left font-semibold text-slate-800 w-1/5 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-2">
                          Name
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-4 text-left font-semibold text-slate-800 w-1/8 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none"
                        onClick={() => handleSort('type')}
                      >
                        <div className="flex items-center gap-2">
                          Type
                          {getSortIcon('type')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-4 text-left font-semibold text-slate-800 w-1/8 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none"
                        onClick={() => handleSort('analyzable')}
                      >
                        <div className="flex items-center gap-2">
                          Analyzable
                          {getSortIcon('analyzable')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-4 text-left font-semibold text-slate-800 w-1/4 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none"
                        onClick={() => handleSort('api_name')}
                      >
                        <div className="flex items-center gap-2">
                          API Name
                          {getSortIcon('api_name')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-4 text-left font-semibold text-slate-800 w-1/8 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-2">
                          Created
                          {getSortIcon('created_at')}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-4 text-left font-semibold text-slate-800 w-1/8 cursor-pointer hover:bg-blue-100 transition-colors duration-200 select-none"
                        onClick={() => handleSort('updated_at')}
                      >
                        <div className="flex items-center gap-2">
                          Updated
                          {getSortIcon('updated_at')}
                        </div>
                      </th>
                      <th className="px-4 py-4 text-center font-semibold text-slate-800 w-24">
                        Instructions
                      </th>
                      <th className="px-4 py-4 text-right font-semibold text-slate-800 w-20">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {emrFields.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                              <DocumentIcon className="w-8 h-8 text-slate-400" />
                            </div>
                            <div>
                              <p className="text-lg font-medium text-slate-900 mb-1">No fields found</p>
                              <p className="text-slate-600">Add your first field to get started.</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      getSortedFields().map((field, idx) => {
                        const normalizedType = (field.type || '').toString().toLowerCase().trim();
                        const typeMeta = fieldTypes.find(t => t.value === normalizedType);

                        return (
                          <tr
                            key={field.id}
                            className={`border-b border-slate-100 transition-all duration-200 ${
                              idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                            } hover:bg-blue-50 hover:shadow-sm cursor-pointer`}
                          >
                            <td className="px-4 py-4 font-medium text-slate-900">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                  {field.name ? field.name[0].toUpperCase() : "F"}
                                </div>
                                <span className="font-semibold truncate">{field.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-slate-700">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm ${
                                typeMeta?.color || 'bg-gray-100 text-gray-800'
                              }`}>
                                {typeMeta?.label || field.type}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-slate-700">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm ${
                              analyzableOptions.find(o => o.value === field.analyzable)?.color || 'bg-gray-100 text-gray-800'
                            }`}>
                              {analyzableOptions.find(o => o.value === field.analyzable)?.label || field.analyzable}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-slate-700">
                            <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded text-slate-600 truncate block">
                              {field.api_name || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-slate-500">
                            <div className="text-sm truncate">
                              {field.created_at ? new Date(field.created_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }) : "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-slate-500">
                            <div className="text-sm truncate">
                              {field.updated_at ? new Date(field.updated_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }) : "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => {
                                setFieldForm({ 
                                  name: field.name, 
                                  type: field.type,
                                  analyzable: field.analyzable || "not_for_analyzing",
                                  api_name: field.api_name || "",
                                  dropdown_values: field.dropdown_values || "",
                                  instructions: field.instructions || ""
                                });
                                setShowEditFieldModal(true);
                              }}
                              className={`p-2 rounded-lg transition-colors ${
                                field.instructions 
                                  ? 'text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100' 
                                  : 'text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100'
                              }`}
                              title={field.instructions ? "Edit instructions" : "Add instructions"}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <div className="flex justify-end gap-0.5">
                              <button
                                onClick={() => openEditFieldModal(field)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:shadow-sm"
                                title="Edit field"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                disabled
                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 cursor-not-allowed opacity-50"
                                title="Delete functionality disabled"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "responses" && (
        <div className="w-full">
          <div className="mb-6">
            <h3 className="text-3xl font-bold text-slate-800 mb-2">EMR Type Responses</h3>
            <p className="text-slate-600 text-lg">
              Create a field name linked to a real EMR type, then choose response 1, response 2, or response 3.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
            <form onSubmit={handleCreateResponseMapping} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Field Name *
                </label>
                <input
                  type="text"
                  value={responseForm.field_name}
                  onChange={(e) =>
                    setResponseForm((prev) => ({ ...prev, field_name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter field name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  EMR Type Name *
                </label>
                <select
                  value={responseForm.emr_type_id}
                  onChange={(e) =>
                    setResponseForm((prev) => ({ ...prev, emr_type_id: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select EMR Type</option>
                  {emrTypes.map((emrType) => (
                    <option key={emrType.id} value={emrType.id}>
                      {emrType.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Response *
                </label>
                <select
                  value={responseForm.response}
                  onChange={(e) =>
                    setResponseForm((prev) => ({ ...prev, response: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {responseOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={responseLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
              >
                {responseLoading ? "Creating..." : "Create"}
              </button>
            </form>

            {responseFormError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {responseFormError}
              </div>
            )}
          </div>

          {responseError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm shadow-sm">
              {responseError}
            </div>
          )}

          <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg p-6 border border-slate-200">
            {responseLoading && responseMappings.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                Loading responses...
              </div>
            ) : responseMappings.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                No response mappings created yet.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-slate-700 border-b border-slate-200">
                      <th className="px-4 py-3 text-left font-semibold text-slate-800">Field Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-800">EMR Type Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-800">Response</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-800">Created</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-800">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responseMappings.map((mapping, index) => (
                      <tr
                        key={mapping.id}
                        className={`border-b border-slate-100 ${
                          index % 2 === 0 ? "bg-white" : "bg-slate-50"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-900">{mapping.field_name}</td>
                        <td className="px-4 py-3 text-slate-700">{mapping.emr_type_name || emrTypes.find((emrType) => emrType.id === mapping.emr_type_id)?.name || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                            {mapping.response_value || mapping.response}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {new Date(mapping.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openDeleteResponseMappingModal(mapping)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                            title="Delete mapping"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">Create EMR Type</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleFormDataChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter EMR type name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Session Type *
                    {isLoadingDocumentationMethods && (
                      <span className="ml-2 text-xs text-blue-600">Loading...</span>
                    )}
                  </label>
                  <select
                    required
                    value={formData.session_type}
                    onChange={(e) => handleDebouncedFormDataChange('session_type', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoadingDocumentationMethods}
                  >
                    <option value="">
                      {isLoadingDocumentationMethods ? 'Loading...' : 'Select a session type...'}
                    </option>
                    {sessionTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Documentation Methods *
                    {isLoadingDocumentationMethods && (
                      <span className="ml-2 text-xs text-blue-600">Loading...</span>
                    )}
                  </label>
                  <select
                    required
                    value={formData.documentation_methods}
                    onChange={(e) => handleDebouncedFormDataChange('documentation_methods', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoadingDocumentationMethods}
                  >
                    <option value="">Select a documentation method...</option>
                    {documentationMethodsOptions}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Files
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {formData.files.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                          <span className="text-sm text-blue-700 truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {formError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {typeof formError === "string" ? formError : JSON.stringify(formError)}
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {formLoading ? "Creating..." : "Create"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEmrType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">Edit EMR Type</h2>
            <form onSubmit={handleEdit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleFormDataChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter EMR type name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Session Type
                    {isLoadingDocumentationMethods && (
                      <span className="ml-2 text-xs text-blue-600">Loading...</span>
                    )}
                  </label>
                  <select
                    value={formData.session_type}
                    onChange={(e) => handleDebouncedFormDataChange('session_type', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoadingDocumentationMethods}
                  >
                    <option value="">
                      {isLoadingDocumentationMethods ? 'Loading...' : 'Select a session type...'}
                    </option>
                    {sessionTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Documentation Methods
                    {isLoadingDocumentationMethods && (
                      <span className="ml-2 text-xs text-blue-600">Loading...</span>
                    )}
                  </label>
                  <select
                    value={formData.documentation_methods}
                    onChange={(e) => handleDebouncedFormDataChange('documentation_methods', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoadingDocumentationMethods}
                  >
                    <option value="">Select a documentation method...</option>
                    {documentationMethodsOptions}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Current Files
                  </label>
                  {keptFiles && keptFiles.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {keptFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-green-50 p-2 rounded">
                          <span className="text-sm text-green-700 truncate">
                            {file.filename || file.name || `File ${index + 1}`}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeKeptFile(index)}
                            className="text-green-500 hover:text-green-700"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-400 text-sm">No files</div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Add New Files
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {formData.files.length > 0 && (
                    <div className="flex flex-col gap-2 mt-2">
                      {formData.files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-blue-50 p-2 rounded">
                          <span className="text-sm text-blue-700 truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {formError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {typeof formError === "string" ? formError : JSON.stringify(formError)}
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {formLoading ? "Saving..." : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEmrType(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showDeleteModal && emrTypeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 text-center">
            <h2 className="text-xl font-bold mb-4 text-red-700">Delete EMR Type</h2>
            <p className="mb-6">Are you sure you want to delete <span className="font-semibold">{emrTypeToDelete.name}</span> and all its files? This action cannot be undone.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleDelete}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={() => { setShowDeleteModal(false); setEmrTypeToDelete(null); }}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Field Modal */}
      {showAddFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Add EMR Field</h2>
              <button
                onClick={() => setShowAddFieldModal(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCreateField}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={fieldForm.name}
                    onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Type *
                  </label>
                  <select
                    required
                    value={fieldForm.type}
                    onChange={(e) => setFieldForm({ ...fieldForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Analyzable
                  </label>
                  <select
                    required
                    value={fieldForm.analyzable}
                    onChange={(e) => setFieldForm({ ...fieldForm, analyzable: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {analyzableOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {fieldForm.type === "dropdown" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Dropdown Values *
                    </label>
                    <textarea
                      required
                      value={fieldForm.dropdown_values}
                      onChange={(e) => setFieldForm({ ...fieldForm, dropdown_values: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Yes&#10;No&#10;Cancel&#10;Maybe&#10;Pending"
                      rows="8"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Enter each value on a new line. You can copy and paste multiple values at once from Excel, Word, or any text editor.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    value={fieldForm.instructions}
                    onChange={(e) => setFieldForm({ ...fieldForm, instructions: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter instructions for this field..."
                    rows="4"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Optional instructions to guide users on how to fill this field.
                  </p>
                </div>
              </div>
              {fieldFormSuccess && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm shadow-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">{fieldFormSuccess}</span>
                  </div>
                </div>
              )}
              {fieldFormError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm shadow-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="font-medium">{fieldFormError}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={fieldFormLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {fieldFormLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    "Create Field"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddFieldModal(false)}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Field Modal */}
      {showEditFieldModal && selectedField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Edit EMR Field</h2>
              <button
                onClick={() => setShowEditFieldModal(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleUpdateField}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={fieldForm.name}
                    onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Type *
                  </label>
                  <select
                    required
                    value={fieldForm.type}
                    onChange={(e) => setFieldForm({ ...fieldForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Analyzable
                  </label>
                  <select
                    required
                    value={fieldForm.analyzable}
                    onChange={(e) => setFieldForm({ ...fieldForm, analyzable: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {analyzableOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {fieldForm.type === "dropdown" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Dropdown Values *
                    </label>
                    <textarea
                      required
                      value={fieldForm.dropdown_values}
                      onChange={(e) => setFieldForm({ ...fieldForm, dropdown_values: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Yes&#10;No&#10;Cancel&#10;Maybe&#10;Pending"
                      rows="8"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Enter each value on a new line. You can copy and paste multiple values at once from Excel, Word, or any text editor.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    value={fieldForm.instructions}
                    onChange={(e) => setFieldForm({ ...fieldForm, instructions: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter instructions for this field..."
                    rows="4"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Optional instructions to guide users on how to fill this field.
                  </p>
                </div>
              </div>
              {fieldFormSuccess && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm shadow-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">{fieldFormSuccess}</span>
                  </div>
                </div>
              )}
              {fieldFormError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm shadow-sm">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="font-medium">{fieldFormError}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={fieldFormLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {fieldFormLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update Field"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditFieldModal(false)}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Field Modal */}
      {showDeleteFieldModal && fieldToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 text-center">
            <h2 className="text-xl font-bold mb-4 text-red-700">Delete EMR Field</h2>
            <p className="mb-6">Are you sure you want to delete the field <span className="font-semibold">"{fieldToDelete.name}"</span>? This action cannot be undone.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setShowDeleteFieldModal(false); setFieldToDelete(null); }}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteField}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Response Mapping Modal */}
      {showDeleteResponseModal && responseMappingToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 text-center shadow-xl border border-slate-200">
            <h2 className="text-xl font-bold mb-3 text-red-700">Delete EMR Type Response</h2>
            <p className="mb-1 text-slate-700">Are you sure you want to delete this response mapping?</p>
            <p className="mb-6 text-slate-600">
              Field: <span className="font-semibold text-slate-800">{responseMappingToDelete.field_name || "-"}</span>
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  setShowDeleteResponseModal(false);
                  setResponseMappingToDelete(null);
                }}
                disabled={responseDeleteLoading}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteResponseMapping}
                disabled={responseDeleteLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {responseDeleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default EMRTypes; 